import { z } from 'zod';
/**
 * Call flows (phase 3, M10): what happens to a call to a number, drawn as a
 * graph of elements connected by their ways out. Stored as data (draft and
 * published versions); the API runs the published version live, step by step.
 */
export const flowNodeTypes = [
    'start',
    'menu',
    'play',
    'hours',
    'queue',
    'callback',
    'voicemail',
    'transfer',
    'callerList',
    'counter',
    'language',
    'gotoFlow',
    'hangup',
];
const id = z.string().min(1).max(64);
export const flowGraphSchema = z.object({
    nodes: z
        .array(z.object({
        id,
        type: z.enum(flowNodeTypes),
        position: z.object({ x: z.number(), y: z.number() }),
        data: z
            .object({
            label: z.string().max(60).optional(),
            promptId: z.uuid().nullable().optional(),
            keys: z
                .array(z.string().regex(/^[0-9*#]$/))
                .max(12)
                .optional(),
            timeoutSeconds: z.number().int().min(1).max(30).optional(),
            hoursId: z.uuid().nullable().optional(),
            queueId: z.uuid().nullable().optional(),
            voicemailFor: z.enum(['queue', 'user']).optional(),
            userId: z.uuid().nullable().optional(),
            email: z.email().nullable().optional(),
            transferTo: z.enum(['user', 'number']).optional(),
            number: z
                .string()
                .regex(/^\+[1-9][0-9]{6,14}$/, 'A number with the country code, e.g. +370 600 00000')
                .nullable()
                .optional(),
            ringSeconds: z.number().int().min(5).max(120).optional(),
            listId: z.uuid().nullable().optional(),
            flowId: z.uuid().nullable().optional(),
            language: z
                .string()
                .regex(/^[a-z]{2}$/)
                .nullable()
                .optional(),
        })
            .strict(),
    }))
        .max(200),
    edges: z.array(z.object({ id, source: id, sourceHandle: z.string().min(1).max(40), target: id })).max(600),
});
/** What an element is called in the editor. */
export const flowNodeNames = {
    start: 'Start',
    menu: 'Menu',
    play: 'Play audio',
    hours: 'Opening hours',
    queue: 'Queue',
    callback: 'Callback',
    voicemail: 'Voicemail',
    transfer: 'Transfer',
    callerList: 'Caller list',
    counter: 'Counter',
    language: 'Language',
    gotoFlow: 'Go to flow',
    hangup: 'Hang up',
};
/** An element's ways out, and whether each must lead somewhere. */
export function flowExits(node, periods = []) {
    switch (node.type) {
        case 'start':
        case 'play':
        case 'callback':
        case 'counter':
        case 'language':
            return [{ id: 'next', label: 'Next', required: true }];
        case 'menu':
            return [
                ...(node.data.keys ?? []).map((k) => ({ id: `key:${k}`, label: k, required: true })),
                { id: 'invalid', label: 'Wrong key', required: true },
                { id: 'timeout', label: 'No key', required: true },
            ];
        case 'hours':
            return [
                { id: 'open', label: 'Open', required: true },
                { id: 'closed', label: 'Closed', required: true },
                ...periods.map((p) => ({ id: `period:${p}`, label: p, required: true })),
            ];
        case 'queue':
            return [
                // After a conversation the call usually just ends.
                { id: 'answered', label: 'Answered', required: false },
                { id: 'timeout', label: 'Waited too long', required: true },
                { id: 'nobody', label: 'Nobody there', required: true },
                { id: 'callback', label: 'Asked for a callback', required: true },
            ];
        case 'transfer':
            return [
                { id: 'answered', label: 'Answered', required: false },
                { id: 'noanswer', label: 'Not answered', required: true },
            ];
        case 'callerList':
            return [
                { id: 'on', label: 'On the list', required: true },
                { id: 'off', label: 'Not on it', required: true },
            ];
        case 'voicemail':
        case 'gotoFlow':
        case 'hangup':
            return [];
    }
}
/**
 * Whether a flow can be published: one start; every element set up (its
 * prompt, queue, hours...); every required way out leading somewhere; every
 * element reachable from the start; and no loop a call can't leave.
 */
export function validateFlow(graph, refs) {
    const problems = [];
    const nodes = new Map(graph.nodes.map((n) => [n.id, n]));
    const starts = graph.nodes.filter((n) => n.type === 'start');
    if (starts.length !== 1)
        problems.push({ nodeId: starts[1]?.id ?? null, message: starts.length === 0 ? 'The flow needs a Start.' : 'A flow has one Start.' });
    for (const node of graph.nodes) {
        const d = node.data;
        const need = (ok, message) => !ok && problems.push({ nodeId: node.id, message });
        switch (node.type) {
            case 'menu':
                need(Boolean(d.promptId && refs.prompts.has(d.promptId)), 'Choose the prompt the menu plays.');
                need((d.keys ?? []).length > 0, 'Choose at least one key.');
                break;
            case 'play':
                need(Boolean(d.promptId && refs.prompts.has(d.promptId)), 'Choose the prompt to play.');
                break;
            case 'hours':
                need(Boolean(d.hoursId && refs.hours.has(d.hoursId)), 'Choose the opening hours.');
                break;
            case 'queue':
                need(Boolean(d.queueId && refs.queues.has(d.queueId)), 'Choose the queue.');
                break;
            case 'callback':
                need(Boolean(d.queueId && refs.queues.has(d.queueId)), 'Choose whose missed-call list it goes on (a queue).');
                break;
            case 'voicemail':
                if (d.voicemailFor === 'user')
                    need(Boolean(d.userId && refs.users.has(d.userId)), 'Choose who gets the messages.');
                else
                    need(Boolean(d.queueId && refs.queues.has(d.queueId)), 'Choose the queue whose members get the messages.');
                if (d.promptId)
                    need(refs.prompts.has(d.promptId), 'The greeting no longer exists.');
                break;
            case 'transfer':
                if (d.transferTo === 'number')
                    need(Boolean(d.number), 'Enter the number to transfer to.');
                else
                    need(Boolean(d.userId && refs.users.has(d.userId)), 'Choose who to transfer to.');
                break;
            case 'callerList':
                need(Boolean(d.listId && refs.lists.has(d.listId)), 'Choose the caller list.');
                break;
            case 'gotoFlow':
                need(Boolean(d.flowId && refs.flows.has(d.flowId)), 'Choose the flow to go to (one that is published).');
                break;
            case 'language':
                need(Boolean(d.language), 'Choose the language.');
                break;
            default:
                break;
        }
        if (node.type === 'menu' && new Set(d.keys).size !== (d.keys ?? []).length)
            need(false, 'Each key once.');
        const exits = flowExits(node, node.type === 'hours' && d.hoursId ? (refs.hours.get(d.hoursId) ?? []) : []);
        for (const exit of exits) {
            if (graph.edges.filter((e) => e.source === node.id && e.sourceHandle === exit.id).length > 1) {
                problems.push({ nodeId: node.id, message: `“${exit.label}” leads to two places: keep one.` });
            }
            const edge = graph.edges.find((e) => e.source === node.id && e.sourceHandle === exit.id);
            if (edge && !nodes.has(edge.target))
                problems.push({ nodeId: node.id, message: `“${exit.label}” leads to an element that is gone.` });
            else if (!edge && exit.required)
                problems.push({ nodeId: node.id, message: `“${exit.label}” leads nowhere.` });
        }
    }
    // Reachable from the start.
    const next = (nodeId) => graph.edges.filter((e) => e.source === nodeId && nodes.has(e.target)).map((e) => e.target);
    const reached = new Set();
    const stack = starts[0] ? [starts[0].id] : [];
    while (stack.length) {
        const current = stack.pop();
        if (reached.has(current))
            continue;
        reached.add(current);
        stack.push(...next(current));
    }
    for (const node of graph.nodes) {
        if (!reached.has(node.id) && node.type !== 'start')
            problems.push({ nodeId: node.id, message: 'No call can reach this element.' });
    }
    // A loop with no way out: elements from which no end (hang up, voicemail,
    // go to flow, an unconnected optional way out, a conversation) can be reached.
    const canEnd = new Set();
    const endsHere = (node) => flowExits(node).length === 0 || node.type === 'queue' || node.type === 'transfer' || flowExits(node).some((x) => !graph.edges.some((e) => e.source === node.id && e.sourceHandle === x.id));
    for (const node of graph.nodes)
        if (endsHere(node))
            canEnd.add(node.id);
    let grew = true;
    while (grew) {
        grew = false;
        for (const node of graph.nodes) {
            if (canEnd.has(node.id))
                continue;
            if (next(node.id).some((t) => canEnd.has(t))) {
                canEnd.add(node.id);
                grew = true;
            }
        }
    }
    for (const node of graph.nodes) {
        if (reached.has(node.id) && !canEnd.has(node.id))
            problems.push({ nodeId: node.id, message: 'Calls here go round in a loop with no way out.' });
    }
    return problems;
}
// ---- API shapes ----
export const callFlowSchema = z.object({
    name: z.string().trim().min(1, 'Give it a name').max(60),
    regionId: z.uuid(),
    graph: flowGraphSchema,
});
export const flowSimulationSchema = z.object({
    /** The draft to try (omitted: the published version). */
    graph: flowGraphSchema.optional(),
    /** When the call comes in (ISO). */
    at: z.iso.datetime({ offset: true }),
    caller: z.string().max(32),
    /** Keys pressed, one per menu reached, in order ("" for none). */
    keys: z.array(z.string().max(1)).max(20).default([]),
    /** How each queue (by element) turns out; default: answered. */
    queueOutcomes: z.record(z.string(), z.enum(['answered', 'timeout', 'nobody', 'callback'])).default({}),
    /** How each transfer (by element) turns out; default: answered. */
    transferOutcomes: z.record(z.string(), z.enum(['answered', 'noanswer'])).default({}),
});
// ---- Prompts, opening hours, caller lists ----
/** Prompt uploads and browser recordings: up to 10 MB. */
export const maxPromptBytes = 10 * 1024 * 1024;
export const promptSchema = z.object({
    name: z.string().trim().min(1, 'Give it a name').max(60),
    regionId: z.uuid(),
});
const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM');
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
/** "open", or a named period that is its own way out (e.g. "Lunch"). */
const periodKind = z.string().trim().min(1).max(30).regex(/^[^:]+$/, 'No colons in a period’s name');
/** A weekly period. Times not covered are closed. */
export const weeklyPeriodSchema = z.object({
    /** 1 Monday ... 7 Sunday. */
    day: z.number().int().min(1).max(7),
    from: time,
    /** At or before from: runs past midnight. */
    to: time,
    kind: periodKind,
});
/** A day or days that differ: a public holiday or closure (no periods: closed all day), or special hours. */
export const hoursExceptionSchema = z
    .object({
    from: date,
    to: date,
    label: z.string().trim().max(60),
    periods: z.array(z.object({ from: time, to: time, kind: periodKind })).max(10),
})
    .refine((e) => e.from <= e.to, { message: 'Ends before it starts', path: ['to'] });
export const openingHoursSchema = z.object({
    name: z.string().trim().min(1, 'Give it a name').max(60),
    regionId: z.uuid(),
    weekly: z.array(weeklyPeriodSchema).max(70),
    exceptions: z.array(hoursExceptionSchema).max(200),
});
export const callerListSchema = z.object({
    name: z.string().trim().min(1, 'Give it a name').max(60),
    regionId: z.uuid(),
    numbers: z
        .array(z.string().trim().regex(/^\+[1-9][0-9]{6,14}$/, 'Numbers with the country code, e.g. +37060000000'))
        .max(5000)
        .transform((numbers) => [...new Set(numbers)]),
});
export const flowNumbersSchema = z.object({ numberIds: z.array(z.uuid()).max(100) });
